import { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
   faChevronUp, 
   faChevronDown,
   faCopy,
   faDownload,
   faCode,
   faCheck,
   faUndo,
   faPlus,
   faTrash
} from "@fortawesome/free-solid-svg-icons";
import { useEditorConfig } from "../../contexts/useEditorConfig";

import "./GeneralEditor.scss";

// Section component for collapsible groups
function EditorSection({ title, expanded, onToggle, children }) {
   return (
      <div className="editor-section">
         <div className="section-header" onClick={onToggle}>
            <span>{title}</span>
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
         </div>
         {expanded && <div className="section-content">{children}</div>}
      </div>
   );
}

export default function GeneralEditor() {
   const { 
      config, 
      isDirty, 
      updateConfig, 
      updateNestedConfig,
      updateDeepConfig,
      updateArrayItem,
      addArrayItem,
      removeArrayItem,
      resetConfig, 
      exportConfig 
   } = useEditorConfig();

   const [expandedSections, setExpandedSections] = useState({
      appearance: true,
      actions: true,
      camera: false,
      titles: false,
      logos: false,
      dimensions: false,
      contact: false,
      disclaimer: false,
      advanced: false
   });

   const [showExportModal, setShowExportModal] = useState(false);
   const [copySuccess, setCopySuccess] = useState(false);

   const toggleSection = (section) => {
      setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
   };

   const handleCopyToClipboard = async () => {
      try {
         await navigator.clipboard.writeText(exportConfig());
         setCopySuccess(true);
         setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
         console.error('Failed to copy:', err);
      }
   };

   const handleDownloadJson = () => {
      const blob = new Blob([exportConfig()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'config.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
   };

   const handleAddLogo = useCallback(() => {
      const newId = (config.customLogos?.length || 0);
      addArrayItem('customLogos', {
         id: newId,
         'custom-logo': '',
         'custom-logo-opacity': '100%',
         'custom-logo-position': 'left',
         'custom-logo-width': '8rem',
         'custom-logo-width-mobile': '4rem'
      });
   }, [config?.customLogos, addArrayItem]);

   if (!config) {
      return <div className="general-editor loading">Loading configuration...</div>;
   }

   return (
      <div className="general-editor">
         <div className="editor-header">
            <h2>General Settings</h2>
            {isDirty && <span className="unsaved-indicator">● Unsaved</span>}
         </div>

         {/* Appearance Section */}
         <EditorSection 
            title="Appearance & Colors" 
            expanded={expandedSections.appearance}
            onToggle={() => toggleSection('appearance')}
         >
            <div className="form-group">
               <label>Background Color / Gradient</label>
               <input
                  type="text"
                  value={config['background-color'] || ''}
                  onChange={(e) => updateConfig('background-color', e.target.value)}
                  placeholder="e.g., #ffffff or linear-gradient(...)"
               />
            </div>

            <div className="form-group">
               <label>Background Image URL</label>
               <input
                  type="text"
                  value={config['background-image'] || ''}
                  onChange={(e) => updateConfig('background-image', e.target.value)}
                  placeholder="https://..."
               />
            </div>

            <div className="form-row">
               <div className="form-group">
                  <label>Brand Color</label>
                  <div className="color-input">
                     <input
                        type="color"
                        value={config['brand-color'] || '#0033a1'}
                        onChange={(e) => updateConfig('brand-color', e.target.value)}
                     />
                     <input
                        type="text"
                        value={config['brand-color'] || ''}
                        onChange={(e) => updateConfig('brand-color', e.target.value)}
                        placeholder="#0033a1"
                     />
                  </div>
               </div>

               <div className="form-group">
                  <label>Hotspot Color</label>
                  <div className="color-input">
                     <input
                        type="color"
                        value={config['hotspot-color'] || '#e42026'}
                        onChange={(e) => updateConfig('hotspot-color', e.target.value)}
                     />
                     <input
                        type="text"
                        value={config['hotspot-color'] || ''}
                        onChange={(e) => updateConfig('hotspot-color', e.target.value)}
                        placeholder="#e42026"
                     />
                  </div>
               </div>
            </div>

            <div className="form-group">
               <label>Title Color</label>
               <div className="color-input">
                  <input
                     type="color"
                     value={config['title-color'] || '#0033a1'}
                     onChange={(e) => updateConfig('title-color', e.target.value)}
                  />
                  <input
                     type="text"
                     value={config['title-color'] || ''}
                     onChange={(e) => updateConfig('title-color', e.target.value)}
                     placeholder="#0033a1"
                  />
               </div>
            </div>

            <div className="form-group">
               <label>Exposure</label>
               <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="3"
                  value={config.exposure ?? 1}
                  onChange={(e) => updateConfig('exposure', parseFloat(e.target.value))}
               />
            </div>
         </EditorSection>

         {/* Action Buttons Section */}
         <EditorSection 
            title="Action Buttons" 
            expanded={expandedSections.actions}
            onToggle={() => toggleSection('actions')}
         >
            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['show-action-icons'] ?? true}
                     onChange={(e) => updateConfig('show-action-icons', e.target.checked)}
                  />
                  Show Action Icons
               </label>
            </div>

            <div className="action-toggles">
               {Object.entries(config.actions || {}).map(([key, value]) => (
                  <div key={key} className="form-group checkbox">
                     <label>
                        <input
                           type="checkbox"
                           checked={value}
                           onChange={(e) => updateNestedConfig('actions', key, e.target.checked)}
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                     </label>
                  </div>
               ))}
            </div>
         </EditorSection>

         {/* Titles Section */}
         <EditorSection 
            title="Titles & Labels" 
            expanded={expandedSections.titles}
            onToggle={() => toggleSection('titles')}
         >
            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['show-watermark'] ?? false}
                     onChange={(e) => updateConfig('show-watermark', e.target.checked)}
                  />
                  Show Watermark
               </label>
            </div>

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['show-title'] ?? false}
                     onChange={(e) => updateConfig('show-title', e.target.checked)}
                  />
                  Show Title
               </label>
            </div>

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['show-product-name'] ?? false}
                     onChange={(e) => updateConfig('show-product-name', e.target.checked)}
                  />
                  Show Product Name
               </label>
            </div>

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['show-variant-name'] ?? false}
                     onChange={(e) => updateConfig('show-variant-name', e.target.checked)}
                  />
                  Show Variant Name
               </label>
            </div>

            <div className="form-group">
               <label>Title Opacity</label>
               <input
                  type="text"
                  value={config['title-opacity'] || '100%'}
                  onChange={(e) => updateConfig('title-opacity', e.target.value)}
                  placeholder="100%"
               />
            </div>
         </EditorSection>

         {/* Custom Logos Section */}
         <EditorSection 
            title="Custom Logos" 
            expanded={expandedSections.logos}
            onToggle={() => toggleSection('logos')}
         >
            {(config.customLogos || []).map((logo, index) => (
               <div key={logo.id ?? index} className="logo-item">
                  <div className="logo-header">
                     <span>Logo {index + 1}</span>
                     <button 
                        className="delete-btn"
                        onClick={() => removeArrayItem('customLogos', index)}
                     >
                        <FontAwesomeIcon icon={faTrash} />
                     </button>
                  </div>
                  
                  <div className="form-group">
                     <label>Image URL</label>
                     <input
                        type="text"
                        value={logo['custom-logo'] || ''}
                        onChange={(e) => updateArrayItem('customLogos', index, { 'custom-logo': e.target.value })}
                        placeholder="https://..."
                     />
                  </div>

                  <div className="form-group">
                     <label>Position</label>
                     <select
                        value={logo['custom-logo-position'] || 'left'}
                        onChange={(e) => updateArrayItem('customLogos', index, { 'custom-logo-position': e.target.value })}
                     >
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="center">Center</option>
                     </select>
                  </div>

                  <div className="form-row">
                     <div className="form-group">
                        <label>Width (Desktop)</label>
                        <input
                           type="text"
                           value={logo['custom-logo-width'] || ''}
                           onChange={(e) => updateArrayItem('customLogos', index, { 'custom-logo-width': e.target.value })}
                           placeholder="10rem"
                        />
                     </div>

                     <div className="form-group">
                        <label>Width (Mobile)</label>
                        <input
                           type="text"
                           value={logo['custom-logo-width-mobile'] || ''}
                           onChange={(e) => updateArrayItem('customLogos', index, { 'custom-logo-width-mobile': e.target.value })}
                           placeholder="5rem"
                        />
                     </div>
                  </div>

                  <div className="form-group">
                     <label>Opacity</label>
                     <input
                        type="text"
                        value={logo['custom-logo-opacity'] || '100%'}
                        onChange={(e) => updateArrayItem('customLogos', index, { 'custom-logo-opacity': e.target.value })}
                        placeholder="100%"
                     />
                  </div>
               </div>
            ))}

            <button className="add-btn" onClick={handleAddLogo}>
               <FontAwesomeIcon icon={faPlus} /> Add Logo
            </button>
         </EditorSection>

         {/* Camera Settings Section */}
         <EditorSection 
            title="Camera & Interaction" 
            expanded={expandedSections.camera}
            onToggle={() => toggleSection('camera')}
         >
            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['auto-rotate'] ?? false}
                     onChange={(e) => updateConfig('auto-rotate', e.target.checked)}
                  />
                  Auto-Rotate
               </label>
            </div>

            {config['auto-rotate'] && (
               <>
                  <div className="form-group">
                     <label>Auto-Rotate Speed</label>
                     <input
                        type="text"
                        value={config['auto-rotate-speed'] || '30deg'}
                        onChange={(e) => updateConfig('auto-rotate-speed', e.target.value)}
                        placeholder="30deg"
                     />
                  </div>

                  <div className="form-group">
                     <label>Auto-Rotate Delay (seconds)</label>
                     <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={config['auto-rotate-delay'] ?? 3}
                        onChange={(e) => updateConfig('auto-rotate-delay', parseFloat(e.target.value))}
                     />
                  </div>
               </>
            )}

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config['disable-pan'] ?? false}
                     onChange={(e) => updateConfig('disable-pan', e.target.checked)}
                  />
                  Disable Pan
               </label>
            </div>

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config.enableWebXr ?? false}
                     onChange={(e) => updateConfig('enableWebXr', e.target.checked)}
                  />
                  Enable WebXR/AR
               </label>
            </div>

            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config.showInteractionPrompt ?? false}
                     onChange={(e) => updateConfig('showInteractionPrompt', e.target.checked)}
                  />
                  Show Interaction Prompt
               </label>
            </div>

            {config.showInteractionPrompt && (
               <div className="form-group">
                  <label>Interaction Prompt Style</label>
                  <select
                     value={config.interactionPromptStyle || 'wiggle'}
                     onChange={(e) => updateConfig('interactionPromptStyle', e.target.value)}
                  >
                     <option value="wiggle">Wiggle</option>
                     <option value="basic">Basic</option>
                  </select>
               </div>
            )}

            <div className="form-group">
               <label>Default Camera Orbit</label>
               <input
                  type="text"
                  value={config['default-camera-orbit'] || ''}
                  onChange={(e) => updateConfig('default-camera-orbit', e.target.value)}
                  placeholder="0deg 75deg 105%"
               />
            </div>

            <div className="form-group">
               <label>Min Camera Orbit</label>
               <input
                  type="text"
                  value={config['min-camera-orbit'] || ''}
                  onChange={(e) => updateConfig('min-camera-orbit', e.target.value)}
                  placeholder="Infinity 0deg auto"
               />
            </div>

            <div className="form-group">
               <label>Max Camera Orbit</label>
               <input
                  type="text"
                  value={config['max-camera-orbit'] || ''}
                  onChange={(e) => updateConfig('max-camera-orbit', e.target.value)}
                  placeholder="Infinity 180deg auto"
               />
            </div>

            <div className="form-group">
               <label>Default Field of View</label>
               <input
                  type="text"
                  value={config['default-fieldOfView'] || ''}
                  onChange={(e) => updateConfig('default-fieldOfView', e.target.value)}
                  placeholder="auto"
               />
            </div>
         </EditorSection>

         {/* Dimensions Section */}
         <EditorSection 
            title="Dimensions Overlay" 
            expanded={expandedSections.dimensions}
            onToggle={() => toggleSection('dimensions')}
         >
            <div className="form-group checkbox">
               <label>
                  <input
                     type="checkbox"
                     checked={config.dimensionsConfig?.enabled ?? false}
                     onChange={(e) => updateDeepConfig('dimensionsConfig.enabled', e.target.checked)}
                  />
                  Enable Dimensions
               </label>
            </div>

            {config.dimensionsConfig?.enabled && (
               <>
                  <div className="form-group checkbox">
                     <label>
                        <input
                           type="checkbox"
                           checked={config.dimensionsConfig?.activeByDefault ?? false}
                           onChange={(e) => updateDeepConfig('dimensionsConfig.activeByDefault', e.target.checked)}
                        />
                        Active by Default
                     </label>
                  </div>

                  <div className="form-group">
                     <label>Unit</label>
                     <select
                        value={config.dimensionsConfig?.dimensionsUnit || 'cm'}
                        onChange={(e) => updateDeepConfig('dimensionsConfig.dimensionsUnit', e.target.value)}
                     >
                        <option value="mm">Millimeters (mm)</option>
                        <option value="cm">Centimeters (cm)</option>
                        <option value="m">Meters (m)</option>
                        <option value="in">Inches (in)</option>
                        <option value="ft">Feet (ft)</option>
                     </select>
                  </div>

                  <div className="form-group">
                     <label>Decimal Places</label>
                     <input
                        type="number"
                        min="0"
                        max="4"
                        value={config.dimensionsConfig?.decimalPlaces ?? 2}
                        onChange={(e) => updateDeepConfig('dimensionsConfig.decimalPlaces', parseInt(e.target.value))}
                     />
                  </div>

                  <div className="form-group">
                     <label>Line Color</label>
                     <div className="color-input">
                        <input
                           type="color"
                           value={config.dimensionsConfig?.lineColor || '#0033a1'}
                           onChange={(e) => updateDeepConfig('dimensionsConfig.lineColor', e.target.value)}
                        />
                        <input
                           type="text"
                           value={config.dimensionsConfig?.lineColor || ''}
                           onChange={(e) => updateDeepConfig('dimensionsConfig.lineColor', e.target.value)}
                        />
                     </div>
                  </div>
               </>
            )}
         </EditorSection>

         {/* Contact Section */}
         <EditorSection 
            title="Contact Information" 
            expanded={expandedSections.contact}
            onToggle={() => toggleSection('contact')}
         >
            <div className="form-group">
               <label>Email</label>
               <input
                  type="email"
                  value={config.contact?.contactMail || ''}
                  onChange={(e) => updateDeepConfig('contact.contactMail', e.target.value)}
                  placeholder="contact@example.com"
               />
            </div>

            <div className="form-group">
               <label>Phone</label>
               <input
                  type="tel"
                  value={config.contact?.contactPhone || ''}
                  onChange={(e) => updateDeepConfig('contact.contactPhone', e.target.value)}
                  placeholder="+1 234 567 890"
               />
            </div>

            <div className="form-group">
               <label>Website Link</label>
               <input
                  type="url"
                  value={config.contact?.contactSiteLink || ''}
                  onChange={(e) => updateDeepConfig('contact.contactSiteLink', e.target.value)}
                  placeholder="https://example.com"
               />
            </div>
         </EditorSection>

         {/* Disclaimer Section */}
         <EditorSection 
            title="Disclaimer" 
            expanded={expandedSections.disclaimer}
            onToggle={() => toggleSection('disclaimer')}
         >
            <div className="form-group">
               <label>Disclaimer Mode</label>
               <select
                  value={config.disclaimerInformation?.disclaimerMode || 'alwaysVisible'}
                  onChange={(e) => updateDeepConfig('disclaimerInformation.disclaimerMode', e.target.value)}
               >
                  <option value="alwaysVisible">Always Visible</option>
                  <option value="popup">Popup</option>
                  <option value="hidden">Hidden</option>
               </select>
            </div>

            <div className="form-group">
               <label>Heading</label>
               <input
                  type="text"
                  value={config.disclaimerInformation?.disclaimerInfoHeading || ''}
                  onChange={(e) => updateDeepConfig('disclaimerInformation.disclaimerInfoHeading', e.target.value)}
                  placeholder="Disclaimer"
               />
            </div>

            <div className="form-group">
               <label>Text</label>
               <textarea
                  value={config.disclaimerInformation?.disclaimerInfoText || ''}
                  onChange={(e) => updateDeepConfig('disclaimerInformation.disclaimerInfoText', e.target.value)}
                  rows="4"
                  placeholder="Disclaimer text..."
               />
            </div>
         </EditorSection>

         {/* Export Modal */}
         {showExportModal && (
            <div className="export-modal-overlay" onClick={() => setShowExportModal(false)}>
               <div className="export-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="export-modal-header">
                     <h3>Export Configuration</h3>
                     <button className="close-btn" onClick={() => setShowExportModal(false)}>×</button>
                  </div>
                  <div className="export-modal-content">
                     <pre>{exportConfig()}</pre>
                  </div>
                  <div className="export-modal-actions">
                     <button className="copy-btn" onClick={handleCopyToClipboard}>
                        <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
                        {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                     </button>
                     <button className="download-btn" onClick={handleDownloadJson}>
                        <FontAwesomeIcon icon={faDownload} />
                        Download JSON
                     </button>
                  </div>
               </div>
            </div>
         )}

         {/* Sticky Save Bar */}
         <div className="save-bar">
            <span className="save-bar-status">
               {isDirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <div className="save-bar-actions">
               <button className="save-bar-btn reset" onClick={resetConfig} disabled={!isDirty}>
                  <FontAwesomeIcon icon={faUndo} />
                  Reset
               </button>
               <button className="save-bar-btn copy-json" onClick={handleCopyToClipboard}>
                  <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
                  {copySuccess ? 'Copied!' : 'Copy JSON'}
               </button>
               <button className="save-bar-btn download-json" onClick={handleDownloadJson}>
                  <FontAwesomeIcon icon={faDownload} />
                  Download
               </button>
               <button className="save-bar-btn preview-json" onClick={() => setShowExportModal(true)}>
                  <FontAwesomeIcon icon={faCode} />
                  Preview
               </button>
            </div>
         </div>
      </div>
   );
}


